import { DEFAULTS, features } from '@/store/features.js'
import { computed } from 'vue'

const featureHeaders = computed(() => {
    const headers = {}
    DEFAULTS.filter(f => f.server).forEach(f => (headers[`x-pi-feature-${f.key}`] = features[f.key]))
    return headers
})

const Status = {
    CONTINUE: 100,
    SWITCHING_PROTOCOLS: 101,
    PROCESSING: 102,
    EARLY_HINTS: 103,

    OK: 200,
    CREATED: 201,
    ACCEPTED: 202,
    NON_AUTHORITATIVE_INFORMATION: 203,
    NO_CONTENT: 204,
    RESET_CONTENT: 205,
    PARTIAL_CONTENT: 206,
    MULTI_STATUS: 207,
    ALREADY_REPORTED: 208,
    IM_USED: 226,

    MULTIPLE_CHOICES: 300,
    MOVED_PERMANENTLY: 301,
    FOUND: 302,
    SEE_OTHER: 303,
    NOT_MODIFIED: 304,
    USE_PROXY: 305,
    TEMPORARY_REDIRECT: 307,
    PERMANENT_REDIRECT: 308,

    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    PAYMENT_REQUIRED: 402,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    METHOD_NOT_ALLOWED: 405,
    NOT_ACCEPTABLE: 406,
    PROXY_AUTHENTICATION_REQUIRED: 407,
    REQUEST_TIMEOUT: 408,
    CONFLICT: 409,
    GONE: 410,
    LENGTH_REQUIRED: 411,
    PRECONDITION_FAILED: 412,
    PAYLOAD_TOO_LARGE: 413,
    URI_TOO_LONG: 414,
    UNSUPPORTED_MEDIA_TYPE: 415,
    RANGE_NOT_SATISFIABLE: 416,
    EXPECTATION_FAILED: 417,
    IM_A_TEAPOT: 418,
    MISDIRECTED_REQUEST: 421,
    UNPROCESSABLE_ENTITY: 422,
    LOCKED: 423,
    FAILED_DEPENDENCY: 424,
    TOO_EARLY: 425,
    UPGRADE_REQUIRED: 426,
    PRECONDITION_REQUIRED: 428,
    TOO_MANY_REQUESTS: 429,
    REQUEST_HEADER_FIELDS_TOO_LARGE: 431,
    UNAVAILABLE_FOR_LEGAL_REASONS: 451,

    INTERNAL_SERVER_ERROR: 500,
    NOT_IMPLEMENTED: 501,
    BAD_GATEWAY: 502,
    SERVICE_UNAVAILABLE: 503,
    GATEWAY_TIMEOUT: 504,
    HTTP_VERSION_NOT_SUPPORTED: 505,
    VARIANT_ALSO_NEGOTIATES: 506,
    INSUFFICIENT_STORAGE: 507,
    LOOP_DETECTED: 508,
    BANDWIDTH_LIMIT_EXCEEDED: 509,
    NOT_EXTENDED: 510,
    NETWORK_AUTHENTICATION_REQUIRED: 511,
}

const listeners = {
    unauthorized: [],
}

export function addEventListener(event, callback) {
    const eventListeners = listeners[event]

    if (eventListeners?.push) {
        eventListeners.push(callback)
    }
}

const apiErrors = [
    Status.UNAUTHORIZED, // 401
    Status.PAYMENT_REQUIRED, // 402
    Status.FORBIDDEN, // 403
    Status.NOT_FOUND, // 404
    Status.CONFLICT, // 409
    Status.UNPROCESSABLE_ENTITY, // 422
]

function addJsonBody(options, body) {
    options.headers || (options.headers = {})
    options.headers['Content-Type'] = 'application/json'
    options.body = JSON.stringify(body)
}

export function appendParams(url, params) {
    params = createParams(params)

    if (params) {
        // we shouldn't be requesting anything with a hash in it,
        // so we are not even bothering to check for it
        url += url.indexOf('?') > -1 ? '&' : '?'
        url += params
    }

    return url
}

export function createParams(params) {
    if (params === null) {
        return null
    }

    const target = new URLSearchParams()

    Object.entries(params).forEach(([key, values]) => {
        if (Array.isArray(values)) {
            key += '[]'
            values.forEach(value => target.append(key, value))
        } else if (values !== null && values !== undefined) {
            target.append(key, values)
        }
    })

    return target.toString() || null
}

const request = async function (url, options = {}, params = {}) {
    options ??= {}
    options.headers = { ...featureHeaders.value, ...options.headers }

    params = createParams(params)

    if (params) {
        // we shouldn't be requesting anything with a hash in it,
        // so we are not even bothering to check for it
        url += url.indexOf('?') > -1 ? '&' : '?'
        url += params
    }

    let response = await fetch(url, options)
    let content = null

    const contentType = response.headers.get('content-type')

    if (contentType?.includes('application/json')) {
        content = await response.json()
    } else if (response.status !== 204) {
        content = await response.blob()
    }

    if (response.ok || apiErrors.includes(response.status)) {
        return { ok: response.ok, status: response.status, contentType, content }
    }

    throw { ok: false, status: response.status, contentType, content }
}

request.get = async function (url, params = {}, options = {}) {
    let response = await request(url, options, params)
    let { ok, content, status } = response
    const restricted = status === Status.PAYMENT_REQUIRED
    if (content && (ok || restricted)) {
        content.restricted = restricted
        return content
    } else if (ok) {
        return content
    }

    if (status === Status.FORBIDDEN || status === Status.NOT_FOUND) {
        return null
    }

    // handle dead cookies
    // TODO: this is dangerous if a request is inadvertently made from the login page
    // MT-1632 created to fix
    if (status === Status.UNAUTHORIZED) {
        listeners.unauthorized.forEach(cb => cb(response, { url, params }))
    }

    throw response
}

request.patch = function (url, body, options = {}, params = null) {
    options ??= {}
    options.method = 'PATCH'

    addJsonBody(options, body)

    return request(url, options, params)
}

request.post = function (url, body, options = {}, params = null) {
    options ??= {}
    options.method = 'POST'

    addJsonBody(options, body)

    return request(url, options, params)
}

request.put = function (url, body, options = {}, params = null) {
    options ??= {}
    options.method = 'PUT'

    addJsonBody(options, body)

    return request(url, options, params)
}

request.delete = async function (url, body = null, options = {}, params = null) {
    options ??= {}
    options.method = 'DELETE'

    addJsonBody(options, body)

    const response = await request(url, options, params)

    if (!response.ok) {
        throw response
    }

    return response
}

export { request, Status }
